import { useCallback, useEffect, useState } from "react";
import MissionOrbit from "./components/CesiumUtils/MissionOrbit";

import OrderCreationModal from "./components/Orders/OrderCreationModal";
import BottomBarOrderAnalytics from "./components/BottomBar/BottomBarOrderAnalytics";

import OrdersView from "./components/Orders/OrdersView";
import { useDispatch, useSelector } from "react-redux";
import MapboxView from "./components/MapboxUtils/MapboxView";

import UnifiedControlPlaneService from "./UnifiedControlPlane.service";
import { useParams } from "react-router-dom";
import { ucp_visualization_presentation } from "./components/CesiumUtils/Visualizations";
import { ORDER_STATUS } from "./UnifiedControlPlane.constants";
import SingleOrderView from "./components/Orders/SingleOrderView";
import SimulateService from "../../service/SimulateService";
import Notifications from "./components/Notifications/Notifications";
import GeneratingSchedule from "./components/Schedule/GeneratingSchedule";
import { getAllScenariosError, getAllScenariosRequest, getAllScenariosSuccess, resetToInitialState, setMapLoading } from '../../../src/store/UnifiedControlPlaneSlice'
import PresimHeader from "./components/Presim/PresimHeader";
import ScheduleDashboard from "./components/Schedule/ScheduleDashboard";
import ScenarioConfig from "./components/Scenario/ScenarioConfig";
import ViewerSettings from "../CommonComponents/ViewerSettings";
import { VIEW_TYPES } from "../../utils/constants";

const UnifiedControlPlane = () => {
  const params = useParams()
  const dispatch = useDispatch();

  const { mission_id } = params;

  const showMapBoxMap = useSelector(state => state.unifiedControlPlane.showMapBoxMap);
  const activeOrdersSection = useSelector(state => state.unifiedControlPlane.activeOrdersSection);
  const isScheduleGenerationComplete = useSelector(state => state.unifiedControlPlane.isScheduleGenerationComplete);
  const orders = useSelector(state => state.unifiedControlPlane.orders);
  const allGroundStations = useSelector(state => state.unifiedControlPlane.allGroundStations);
  const groups = useSelector(state => state.unifiedControlPlane.groups);
  const showSingleOrderView = useSelector(state => state.unifiedControlPlane.showSingleOrderView);
  const notification = useSelector(state => state.unifiedControlPlane.notification);
  const mapLoading = useSelector(state => state.unifiedControlPlane.mapLoading);
  const scheduleDashboardView = useSelector(state => state.unifiedControlPlane.scheduleDashboardView);
  const generatedScheduleId = useSelector(state => state.unifiedControlPlane.generatedScheduleId);
  const allScenarios = useSelector(state => state.unifiedControlPlane.allScenarios);
  const scenarioId = useSelector(state => state.unifiedControlPlane.scenarioId)

  const [isOrdersBarVisible, setIsOrdersBarVisible] = useState(true);
  const [isOrderCreationModal, setIsOrderCreationModal] = useState(false);
  const [isExpandedOrdersBar, setIsExpandedOrdersBar] = useState(false);
  const [bottomBarComponent, setBottomBarComponent] = useState({ Order: true, Analytics: false, Policy: false });
  const [ucpViewer, setUcpViewer] = useState();

  const scenarioCurrentTime = useSelector(state => state.unifiedControlPlane.scenarioCurrentTime);
  const simulationClockFlag = useSelector(state => state.unifiedControlPlane.simulationClockFlag)

  const props = {
    isOrdersBarVisible,
    isOrderCreationModal,
    isExpandedOrdersBar,
    bottomBarComponent,
    setIsOrdersBarVisible,
    setIsOrderCreationModal,
    setIsExpandedOrdersBar,
    setBottomBarComponent,
  };

  const getListOfAois = (order) => {
    let listOfAOI = []

    order?.forEach((currentOrder, index) => {
      if (currentOrder?.spec?.geofeature?.features[0]?.geometry?.type === "Polygon") {
        listOfAOI.push({
          name: currentOrder?.spec?.name,
          areaOfInterestId: currentOrder?.spec?.geofeature?.features[0]?.id,
          polygon: currentOrder?.spec?.geofeature?.features[0]?.geometry?.coordinates
        })
      }
      else if (currentOrder?.spec?.geofeature?.features[0]?.geometry?.type === "Point") {
        listOfAOI.push({
          name: currentOrder?.spec?.name,
          areaOfInterestId: currentOrder?.spec?.geofeature?.features[0]?.id,
          point: currentOrder?.spec?.geofeature?.features[0]?.geometry?.coordinates
        })
      }
      currentOrder?.status?.coverage?.coveredStrips?.forEach((strip, index) => {
        listOfAOI.push(
          {
            name: index,
            areaOfInterestId: index,
            polygon: strip.geoJSON.coordinates
          }
        )
      })
    })
    return listOfAOI;
  }

  const setActiveAoiDataSource = useCallback(() => {
    switch (activeOrdersSection) {
      case ORDER_STATUS.CURRENT:
        ucp_visualization_presentation().show_datasource(ucpViewer, 'Current_Order')
        ucp_visualization_presentation().hide_datasource(ucpViewer, 'Executed_Order')
        ucp_visualization_presentation().hide_datasource(ucpViewer, 'Pending_Order')
        break;
      case ORDER_STATUS.EXECUTED:
        ucp_visualization_presentation().show_datasource(ucpViewer, 'Executed_Order')
        ucp_visualization_presentation().hide_datasource(ucpViewer, 'Current_Order')
        ucp_visualization_presentation().hide_datasource(ucpViewer, 'Pending_Order')
        break;
      case ORDER_STATUS.PENDING:
        ucp_visualization_presentation().show_datasource(ucpViewer, 'Pending_Order')
        ucp_visualization_presentation().hide_datasource(ucpViewer, 'Executed_Order')
        ucp_visualization_presentation().hide_datasource(ucpViewer, 'Current_Order')
        break;
      default:
        ucp_visualization_presentation().hide_datasource(ucpViewer, 'Pending_Order')
        ucp_visualization_presentation().hide_datasource(ucpViewer, 'Executed_Order')
        ucp_visualization_presentation().hide_datasource(ucpViewer, 'Current_Order')
        break
    }
  }, [activeOrdersSection, ucpViewer])

  const getScenarioStatusById = (scenarioId) => {
    const scenario = allScenarios?.find(scenario => scenario.scenarioId === scenarioId);
    return scenario ? scenario.status : null;
  }

  const getFilteredScenarios = (scenarios = []) => {
    const validScenarios = ["ReadyForSimulation", "SimulationRunning", "SimulationCompleted"];
    return scenarios.filter((scenario) => validScenarios.includes(scenario?.status))
  }

  useEffect(() => {
    if (!mission_id) return;
    if (mapLoading) {
      dispatch(getAllScenariosRequest());

      SimulateService.getALLScenariosOrchestrate(mission_id)
        .then((res) => {
          const data = res?.data?.data || res?.data
          if (data) {
            const filteredScenarios = getFilteredScenarios(data)
            dispatch(getAllScenariosSuccess(filteredScenarios));
          }
          dispatch(setMapLoading(false));
        }).catch((err) => {
          dispatch(getAllScenariosError(err?.response?.data?.message || 'Something went wrong'));
          dispatch(setMapLoading(false));
        })
    }
  }, [dispatch, mission_id, mapLoading]);

  useEffect(() => {
    if (ucpViewer) {
      dispatch(UnifiedControlPlaneService.getGroundStationsFromMission(mission_id))
      dispatch(UnifiedControlPlaneService.getOrders());
      dispatch(UnifiedControlPlaneService.getCurrentSchedule(mission_id));
    }
  }, [dispatch, mission_id, ucpViewer])

  useEffect(() => {
    if (scenarioId) {
      const scenarioStatus = getScenarioStatusById(scenarioId);
      let propagationType = '';
      if (scenarioStatus === "ReadyForSimulation") {
        propagationType = 'scenario';
      } else {
        propagationType = 'dashboard';
      }
      dispatch(UnifiedControlPlaneService.getGroupFromMultiSat(mission_id, scenarioId, propagationType))
    }
  }, [dispatch, mission_id, scenarioId, ucpViewer])

  useEffect(() => {
    ucp_visualization_presentation().init_all_aois(getListOfAois(orders?.current), ucpViewer, 'Current_Order')
    ucp_visualization_presentation().init_all_aois(getListOfAois(orders?.executed), ucpViewer, 'Executed_Order')
    ucp_visualization_presentation().init_all_aois(getListOfAois(orders?.pending), ucpViewer, 'Pending_Order')
  }, [orders, ucpViewer])

  useEffect(() => {
    if (ucpViewer && ucpViewer?.dataSources?.length > 0) {
      setActiveAoiDataSource()
    }
  }, [setActiveAoiDataSource, ucpViewer])

  useEffect(() => {
    if (allGroundStations && ucpViewer) {
      ucp_visualization_presentation().init_all_gs(allGroundStations, ucpViewer)
    }
    if (groups && ucpViewer && simulationClockFlag) {
      ucp_visualization_presentation().init_all_satellites(groups, ucpViewer, scenarioCurrentTime)
    }
  }, [allGroundStations, groups])

  useEffect(() => {
    let Interval;
    if (!isScheduleGenerationComplete && generatedScheduleId) {
      Interval = setInterval(() => {
        dispatch(UnifiedControlPlaneService.getSchedule(mission_id, generatedScheduleId))
      }, 10000)
      return () => clearInterval(Interval);
    }
  }, [dispatch, mission_id, isScheduleGenerationComplete, generatedScheduleId])

  useEffect(() => {
    return () => {
      // resetting redux store for UCP
      dispatch(resetToInitialState());
      sessionStorage.setItem('map_details', JSON.stringify({}));
    }
  }, [dispatch]);

  return (
    <>
      {(!showMapBoxMap && bottomBarComponent.Order) && <MissionOrbit ucpViewer={ucpViewer} setUcpViewer={setUcpViewer} />}
      {(!showMapBoxMap && bottomBarComponent.Order) && isOrdersBarVisible && !scheduleDashboardView && (
        showSingleOrderView ?
          <SingleOrderView props={props} ucpViewer={ucpViewer} /> :
          <OrdersView props={props} ucpViewer={ucpViewer} scenarioId={scenarioId} mission_id={mission_id} />
      )}
      {showMapBoxMap && <MapboxView />}
      {isOrderCreationModal && bottomBarComponent.Order && <OrderCreationModal props={props} />}
      {!(showSingleOrderView || scheduleDashboardView) && <BottomBarOrderAnalytics props={props} ucpViewer={ucpViewer} />}
      {(notification.isError || notification.isSuccess) && <Notifications />}
      {(notification.showGeneratingPopup) && <GeneratingSchedule />}
      {!showMapBoxMap && scheduleDashboardView && <PresimHeader scenarioId={scenarioId} mission_id={mission_id} />}
      {!showMapBoxMap && !scheduleDashboardView && !showSingleOrderView && <ScenarioConfig mission_id={mission_id} />}
      {scheduleDashboardView && <ScheduleDashboard props={props} ucpViewer={ucpViewer} scenarioId={scenarioId} mission_id={mission_id} />}
      {<ViewerSettings viewer={ucpViewer} viewerId={scenarioId ?? "default-ucp"} viewType={VIEW_TYPES.UCP} />}
    </>
  );
};

export default UnifiedControlPlane;
